/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { MyProfileEmailWidgetEditorTranslations } from '../../interfaces/myprofile-email-widget.interfaces'
import { MyProfileEmailEditorService } from './myprofile-email-editor.service'
import { EmailCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/email/email.service'

@Component({
  selector: 'myprofile-email-editor',
  templateUrl: './myprofile-email-editor.component.html',
  host: { class: 'profile:block' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileEmailEditorComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder)

  @Input() email!: EmailCommunicationDto
  @Input() translations!: MyProfileEmailWidgetEditorTranslations
  @Input() isFirstEmail = false
  @Input() set emailAlreadyExistsError(value: boolean) {
    this._emailAlreadyExistsError = value
    this.applyEmailAlreadyExistsError()
  }
  @Output() leaveEditModeEvent = new EventEmitter()
  @Output() deleteEmailAddressEvent = new EventEmitter<EmailCommunicationDto>()
  @Output() updateEmailAddressEvent = new EventEmitter<EmailCommunicationDto>()
  @Output() goToChangePorscheIdPageEvent = new EventEmitter()

  public formGroup!: UntypedFormGroup

  private _emailAlreadyExistsError = false

  get emailFormControlErrorMessage() {
    const emailFormControl = this.formGroup.get('email')

    if (!emailFormControl || emailFormControl.valid || !emailFormControl.touched) {
      return undefined
    }

    if (emailFormControl.errors?.['required']) {
      return this.translations.inputErrorMessageRequired
    }

    if (emailFormControl.errors?.['email']) {
      return this.translations.inputErrorMessageEmail
    }

    if (emailFormControl.errors?.['alreadyExists']) {
      return this.translations.inputErrorMessageAlreadyExists
    }
  }

  static triggerFormValidationErrors(formGroup: UntypedFormGroup) {
    Object.entries(formGroup.controls)
      .filter(([_, control]) => !control.disabled)
      .forEach(([controlName]) => {
        const element = document.querySelector(`[name="${controlName}"]`) as HTMLElement | null
        if (element) {
          element.focus()
          element.blur()
        }
      })

    Object.values(formGroup.controls)
      .filter((ctrl) => !ctrl.disabled)
      .forEach((ctrl) => {
        ctrl.markAllAsTouched()
      })
  }

  public ngOnInit(): void {
    if (this.email) {
      this.formGroup = this.formBuilder.group({
        email: [this.email.email, [Validators.required, Validators.email]],
        emailId: this.email.emailId,
        usage: this.email.usage,
        isStandard: [{ value: this.email.isStandard, disabled: this.email.isStandard }],
        isVerified: this.email.isVerified,
        isPorscheId: this.email.isPorscheId,
      })
    } else {
      this.formGroup = this.formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        emailId: null,
        usage: 'home',
        isStandard: [{ value: this.isFirstEmail, disabled: this.isFirstEmail }],
        isVerified: false,
        isPorscheId: false,
      })
    }

    this.applyEmailAlreadyExistsError()
    this.formGroup.get('email')!.valueChanges.subscribe(() => this.clearEmailAlreadyExistsError())
  }

  private applyEmailAlreadyExistsError(): void {
    const control = this.formGroup?.get('email')
    if (!control) {
      return
    }
    if (this._emailAlreadyExistsError) {
      control.setErrors({ ...(control.errors ?? {}), alreadyExists: true })
      control.markAsTouched()
    } else {
      this.clearEmailAlreadyExistsError()
    }
  }

  private clearEmailAlreadyExistsError(): void {
    const control = this.formGroup?.get('email')
    if (!control?.errors?.['alreadyExists']) {
      return
    }
    const remaining = { ...control.errors }
    delete remaining['alreadyExists']
    control.setErrors(Object.keys(remaining).length ? remaining : null)
  }

  public delete() {
    this.deleteEmailAddressEvent.emit(this.email)
  }

  public cancel() {
    this.leaveEditModeEvent.emit()
  }

  public submitForm(formGroup: UntypedFormGroup) {
    if (formGroup.invalid) {
      MyProfileEmailEditorComponent.triggerFormValidationErrors(formGroup)
      return
    }

    if (this.email !== undefined) {
      const wasVerified = this.email.isVerified
      const isSame = this.email.email === this.formGroup.get('email')!.value
      this.formGroup.get('isVerified')!.setValue(wasVerified && isSame)
      this.updateEmailAddressEvent.emit(MyProfileEmailEditorService.prepareEmailPayload(formGroup))
    } else {
      this.updateEmailAddressEvent.emit(MyProfileEmailEditorService.prepareEmailPayload(formGroup))
    }
  }

  public goToChangePorscheIdPage() {
    this.goToChangePorscheIdPageEvent.emit()
  }

  public getSubmitButtonLabel() {
    if (this.formGroup.get('isStandard')!.value) {
      if (this.email && this.formGroup.get('email')!.value === this.email.email && this.email.isVerified) {
        return this.translations.saveButtonLabel
      }
      return this.translations.saveAndVerifyButtonLabel
    }

    return this.translations.saveButtonLabel
  }
}
